/**
 * P4 — ongoing HealthKit workout import driver.
 *
 * Renders nothing; mounted once at the app root (beside HealthSyncMount) so it
 * spans the whole signed-in native session. On app open + every foreground it
 * imports the trailing 7 days of running workouts, gated on native + signed-in
 * + the `hk_import_v1` flag + the signed-in account's own opt-in (B57 —
 * `isImportEnabled(userId)`, per account, never per device).
 * The 30-day first-enable backfill runs from the Settings toggle directly.
 *
 * Best-effort / no-throw — importHealthKitWorkouts swallows every failure.
 */

import { useEffect } from 'react';
import { useAuthContext } from '../../contexts/AuthContext';
import { useAuthFetch } from '../../hooks/useAuthFetch';
import { useServerFlag } from '../../hooks/useServerFlag';
import { isNativeApp } from '../../lib/platform';
import { importHealthKitWorkouts, isImportEnabled } from '../../lib/health/workoutImport';

/** Trailing window for the ongoing foreground import. */
const FOREGROUND_DAYS = 7;

export function WorkoutImportMount(): null {
  const { user } = useAuthContext();
  const fetchAuthed = useAuthFetch();
  const importOn = useServerFlag('hk_import_v1');

  useEffect(() => {
    if (!isNativeApp() || !user || !importOn) return;
    let alive = true;

    // Re-check the opt-in at fire time (the toggle can flip mid-session).
    // B57 — the opt-in is PER ACCOUNT: ask about the athlete who is actually
    // signed in right now, never "is this device armed". The effect is keyed on
    // `user`, so a sign-out → sign-in tears this listener down and re-arms it
    // against the new account's own answer.
    const run = () => {
      if (alive && isImportEnabled(user.id)) void importHealthKitWorkouts(fetchAuthed, FOREGROUND_DAYS, user.id);
    };

    run(); // app open

    let remove: (() => void) | null = null;
    void (async () => {
      try {
        const { App } = await import('@capacitor/app');
        const handle = await App.addListener('appStateChange', ({ isActive }) => {
          if (isActive) run();
        });
        remove = () => void handle.remove();
      } catch {
        /* @capacitor/app unavailable — the mount-time import still ran. */
      }
    })();

    return () => {
      alive = false;
      remove?.();
    };
  }, [user, importOn, fetchAuthed]);

  return null;
}
